import {isFeatureEnabled} from 'feature-flags';
import {Box, HStack, Show} from 'platform/foundation';
import styled, {css, useTheme} from 'styled-components';

import {lazy, Suspense, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {isNil} from 'ramda';

import {MenuItemProps, UserSidebarIconSize} from '@dms/api/shared';
import {DevSettings} from '@dms/dev-settings';
import {featureFlags} from '@dms/feature-flags';
import {TelephonyCallIndicator} from '@dms/features/telephony-call';
import {sourcingRoutes} from '@dms/routes';
import {useHasMultipleBranches, usePermissions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {TOP_BAR_ITEM_SIZE} from '../constants/topBarItemSize';
import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {AuthProfileSelect} from './AuthProfileSelect';
import {AvailabilityStatusMenu} from './availabilityStatus/AvailabilityStatusMenu';
import {AvailabilityStatusSelect} from './availabilityStatus/AvailabilityStatusSelect';
import {AwayStatusMenu} from './AwayStatusMenu';
import {AwayStatusSelect} from './AwayStatusSelect';
import {BranchSelect} from './BranchSelect';
import {HeaderContent} from './HeaderContent';
import {HideOnMobileNavigation} from './HideOnMobileNavigation';
import {Notifications} from './Notifications';
import {ReleasePopup} from './ReleasePopup';
import {Settings} from './Settings';
import {TaskCenter} from './TaskCenter';
import {UpdateAvailableDot} from './UpdateAvailableDot';
import {UserAvatar} from './UserAvatar';

const GlobalSearchHeroBar = lazy(() =>
  import('@dms/features/global-search').then((module) => ({
    default: module.GlobalSearchHeroBar,
  }))
);

const NAVBAR_SEARCH_FIELD_WIDTH_STEPS = 80;
const NAVBAR_SEARCH_ICON_FOOTPRINT_STEPS = 9;
const NAVBAR_SEARCH_COLLAPSE_THRESHOLD_STEPS = 50;
const CONTENT_CELL_GAP_STEPS = 4;
const TOP_BAR_GRID_GAP_STEPS = 4;
const HEADER_CELL_FOCUS_RING_PADDING_STEPS = 1;
const SELECT_RELOCATION_HYSTERESIS_STEPS = 6;
const THEME_SIZE_STEP_PX = 4;
const NAVBAR_SEARCH_FIELD_WIDTH_PX = NAVBAR_SEARCH_FIELD_WIDTH_STEPS * THEME_SIZE_STEP_PX;
const NAVBAR_SEARCH_ICON_FOOTPRINT_PX = NAVBAR_SEARCH_ICON_FOOTPRINT_STEPS * THEME_SIZE_STEP_PX;
const CONTENT_CELL_GAP_PX = CONTENT_CELL_GAP_STEPS * THEME_SIZE_STEP_PX;
const SELECT_RELOCATION_HYSTERESIS_PX = SELECT_RELOCATION_HYSTERESIS_STEPS * THEME_SIZE_STEP_PX;
const NAVBAR_SEARCH_COLLAPSE_THRESHOLD_PX =
  NAVBAR_SEARCH_COLLAPSE_THRESHOLD_STEPS * THEME_SIZE_STEP_PX;

const TopBarGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: ${({theme}) => theme.getSize(TOP_BAR_GRID_GAP_STEPS)};
  width: 100%;
  height: 100%;
`;

const ContentCell = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  height: 100%;
  min-width: 0;
  gap: ${({theme}) => theme.getSize(CONTENT_CELL_GAP_STEPS)};
  overflow: visible;
`;

const HeaderCell = styled.div<{$canGrow: boolean}>`
  display: flex;
  align-items: center;
  flex: ${({$canGrow}) => ($canGrow ? '1 0 auto' : '0 0 auto')};
  height: 100%;
  padding: 0 ${({theme}) => theme.getSize(HEADER_CELL_FOCUS_RING_PADDING_STEPS)};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SearchCell = styled.div<{$isCollapsed: boolean}>`
  position: relative;
  z-index: ${({theme}) => theme.zIndices.HEADER_POPPER};
  min-width: ${({theme}) => theme.getSize(NAVBAR_SEARCH_ICON_FOOTPRINT_STEPS)};
  overflow: visible;
  ${({$isCollapsed, theme}) =>
    $isCollapsed
      ? css`
          flex: 0 0 auto;
          width: ${theme.getSize(NAVBAR_SEARCH_ICON_FOOTPRINT_STEPS)};
        `
      : css`
          flex: 0 1 auto;
          width: ${theme.getSize(NAVBAR_SEARCH_FIELD_WIDTH_STEPS)};
        `}
`;

const ActionsCell = styled.div`
  grid-column: 2;
  justify-self: end;
`;

const SelectMeasureCell = styled.div`
  display: flex;
  flex: none;
`;

interface TopBarProps extends RequiredTestIdProps {
  iconSize: UserSidebarIconSize;
  activeModule?: MenuItemProps;
  settingsModule?: MenuItemProps;
}

export function TopBar(props: TopBarProps) {
  const {colors} = useTheme();
  const location = useLocation();
  const isMobileNavigation = useIsMobileNavigation();
  const topBarGridRef = useRef<HTMLDivElement>(null);
  const contentCellRef = useRef<HTMLDivElement>(null);
  const headerCellRef = useRef<HTMLDivElement>(null);
  const actionsCellRef = useRef<HTMLDivElement>(null);
  const authProfileSelectRef = useRef<HTMLDivElement>(null);
  const branchSelectRef = useRef<HTMLDivElement>(null);
  const globalSearchVisibilityRef = useRef<HTMLSpanElement>(null);
  const cachedSelectWidthsRef = useRef({authProfile: 0, branch: 0});
  const resizeObserverRef = useRef<ResizeObserver>(null);
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [areTopBarSelectsRelocated, setAreTopBarSelectsRelocated] = useState(false);

  const isSourcingHome = location.pathname.includes(sourcingRoutes.home);
  const isGlobalSearchVisible =
    isFeatureEnabled(featureFlags.GLOBAL_SEARCH) && !isSourcingHome && !isMobileNavigation;
  const {hasMultipleBranches} = useHasMultipleBranches();

  const setupTopBarResizeObserver = () => {
    const contentCell = contentCellRef.current;
    const headerCell = headerCellRef.current;
    const actionsCell = actionsCellRef.current;

    if (isNil(contentCell) || isNil(headerCell) || isNil(actionsCell)) {
      return;
    }

    const updateTopBarLayout = () => {
      const topBarGrid = topBarGridRef.current;
      const currentContentCell = contentCellRef.current;
      const currentHeaderCell = headerCellRef.current;
      const currentActionsCell = actionsCellRef.current;
      const authProfileSelect = authProfileSelectRef.current;
      const branchSelect = branchSelectRef.current;

      if (
        isNil(topBarGrid) ||
        isNil(currentContentCell) ||
        isNil(currentHeaderCell) ||
        isNil(currentActionsCell) ||
        isNil(globalSearchVisibilityRef.current)
      ) {
        setAreTopBarSelectsRelocated(false);
        setIsSearchCollapsed(false);
        setIsSearchHidden(false);
        return;
      }

      const authProfileSelectWidthPx = authProfileSelect?.offsetWidth ?? 0;
      const branchSelectWidthPx = branchSelect?.offsetWidth ?? 0;
      const topBarGridWidthPx = topBarGrid.clientWidth;
      const contentCellWidthPx = currentContentCell.clientWidth;
      const headerCellWidthPx = currentHeaderCell.offsetWidth;
      const actionsCellWidthPx = currentActionsCell.offsetWidth;

      if (!isNil(authProfileSelect) || !isNil(branchSelect)) {
        cachedSelectWidthsRef.current.authProfile = authProfileSelectWidthPx;
        cachedSelectWidthsRef.current.branch = branchSelectWidthPx;
      }

      const availableSearchWidthPx = contentCellWidthPx - headerCellWidthPx - CONTENT_CELL_GAP_PX;

      setAreTopBarSelectsRelocated((wasRelocated) => {
        const cachedSelectWidthPx =
          cachedSelectWidthsRef.current.authProfile + cachedSelectWidthsRef.current.branch;
        const clusterFullWidthPx = wasRelocated
          ? actionsCellWidthPx + cachedSelectWidthPx
          : actionsCellWidthPx;
        const requiredWidthPx =
          headerCellWidthPx +
          CONTENT_CELL_GAP_PX +
          NAVBAR_SEARCH_FIELD_WIDTH_PX +
          clusterFullWidthPx;
        const relocationBoundaryPx =
          requiredWidthPx + (wasRelocated ? SELECT_RELOCATION_HYSTERESIS_PX : 0);

        return topBarGridWidthPx < relocationBoundaryPx;
      });
      setIsSearchCollapsed(availableSearchWidthPx < NAVBAR_SEARCH_COLLAPSE_THRESHOLD_PX);
      setIsSearchHidden(availableSearchWidthPx < NAVBAR_SEARCH_ICON_FOOTPRINT_PX);
    };

    const resizeObserver = new ResizeObserver(updateTopBarLayout);
    resizeObserver.observe(contentCell);
    resizeObserver.observe(headerCell);
    resizeObserver.observe(actionsCell);
    resizeObserverRef.current = resizeObserver;
  };

  const cleanupTopBarResizeObserver = () => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
  };

  useOnMount(setupTopBarResizeObserver, cleanupTopBarResizeObserver);

  const [hasReadSettingsPermission] = usePermissions({permissionKeys: ['readSettings']});
  const [canReadTasks] = usePermissions({
    permissionKeys: ['readTask'],
  });

  return (
    <>
      <meta name="theme-color" content={colors.palettes.neutral[900][100]} />
      <Box
        zIndex="HEADER"
        paddingLeft={[0, 0, 4]}
        height="100%"
        width="100%"
        overflow="hidden"
        backgroundColor={isMobileNavigation ? 'palettes.neutral.900.100' : 'general.white'}
        borderColor={isMobileNavigation ? undefined : 'general.separator'}
        borderBottom={isMobileNavigation ? undefined : '1px solid'}
      >
        <TopBarGrid ref={topBarGridRef}>
          <Show when={isGlobalSearchVisible}>
            <span ref={globalSearchVisibilityRef} hidden />
          </Show>
          <ContentCell ref={contentCellRef}>
            <HeaderCell ref={headerCellRef} $canGrow={!isGlobalSearchVisible}>
              <HeaderContent
                title={props.activeModule?.layoutProps?.title}
                data-testid={suffixTestId('header-content', props)}
              />
            </HeaderCell>
            <HideOnMobileNavigation>
              <Show whenFeatureEnabled={featureFlags.GLOBAL_SEARCH} when={!isSourcingHome}>
                <Show when={!isSearchHidden}>
                  <SearchCell $isCollapsed={isSearchCollapsed}>
                    <Suspense fallback={null}>
                      <GlobalSearchHeroBar isCollapsed={isSearchCollapsed} />
                    </Suspense>
                  </SearchCell>
                </Show>
              </Show>
            </HideOnMobileNavigation>
          </ContentCell>
          <ActionsCell ref={actionsCellRef}>
            <HStack shrink={0} align="center" justify="flex-end">
              <Show whenFeatureEnabled={featureFlags.AUTH_PROFILE_SELECTOR} when={!isSourcingHome}>
                <HideOnMobileNavigation>
                  <Show when={!areTopBarSelectsRelocated}>
                    <SelectMeasureCell ref={authProfileSelectRef}>
                      <AuthProfileSelect />
                    </SelectMeasureCell>
                  </Show>
                </HideOnMobileNavigation>
              </Show>
              <Show when={hasMultipleBranches}>
                <HideOnMobileNavigation>
                  <Show when={!areTopBarSelectsRelocated}>
                    <SelectMeasureCell ref={branchSelectRef}>
                      <BranchSelect />
                    </SelectMeasureCell>
                  </Show>
                </HideOnMobileNavigation>
              </Show>
              <TelephonyCallIndicator
                isCompact={areTopBarSelectsRelocated || isMobileNavigation}
                idleControl={
                  <HideOnMobileNavigation>
                    <Show whenFeatureEnabled={featureFlags.USER_ABSENCE}>
                      <AvailabilityStatusSelect />
                    </Show>
                    <Show whenFeatureDisabled={featureFlags.USER_ABSENCE}>
                      <AwayStatusSelect />
                    </Show>
                  </HideOnMobileNavigation>
                }
                renderAvailabilityMenu={(close) =>
                  isFeatureEnabled(featureFlags.USER_ABSENCE) ? (
                    <AvailabilityStatusMenu onClose={close} />
                  ) : (
                    <AwayStatusMenu />
                  )
                }
                data-testid="layout-header"
              />
              <Show when={canReadTasks}>
                <TaskCenter iconSize={props.iconSize} />
              </Show>
              <Notifications iconSize={props.iconSize} data-testid="layout-header" />
              <HideOnMobileNavigation>
                <ReleasePopup data-testid="layout-header" iconSize={props.iconSize} />
                <Show when={hasReadSettingsPermission}>
                  <Settings
                    settingsModule={props.settingsModule}
                    iconSize={props.iconSize}
                    data-testid="layout-header"
                  />
                </Show>
                <Show when={process.env.ENV_TYPE === 'dev'}>
                  <DevSettings
                    width={TOP_BAR_ITEM_SIZE}
                    height={TOP_BAR_ITEM_SIZE}
                    iconSize={props.iconSize}
                    data-testid="layout-header"
                  />
                </Show>
              </HideOnMobileNavigation>
              <UserAvatar
                areTopBarSelectsRelocated={areTopBarSelectsRelocated}
                data-testid="layout-header"
              />
              <UpdateAvailableDot data-testid="layout-header" />
            </HStack>
          </ActionsCell>
        </TopBarGrid>
      </Box>
    </>
  );
}
